import {
  Activity,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Coins,
  Cpu,
  Paperclip,
  Send,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { AttachmentKind, ThreadTodoItem } from "@/api/types";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/ui/stat-card";
import { useThreadMetrics } from "@/features/observability/hooks/useObservability";
import { useThreadAttachments } from "@/features/threads/hooks/useAttachments";
import { useThreadTodos } from "@/features/threads/hooks/useThreads";
import { formatCostUsd } from "@/lib/cost";
import { cn } from "@/lib/utils";

import { AttachmentBubble } from "./AttachmentBubble";
import { TodoList } from "./TodoList";

interface ChatSidePanelProps {
  threadId: string | undefined;
  /** Chat de team session -> renderiza lista de TODOs do deep agent. */
  isTeamSession?: boolean;
  /** Sinaliza streaming em curso (mostra indicador piscante). */
  streaming?: boolean;
  /**
   * TODOs vindos do SSE ao vivo (event ``todos_updated``). Quando
   * undefined, cai pro hook ``useThreadTodos`` (que busca via REST).
   */
  liveTodos?: ThreadTodoItem[];
  /**
   * Se mudar de valor, dispara refetch das metricas. Padrao: aumentar
   * um counter no parent toda vez que ``done`` chegar pelo SSE.
   */
  refreshKey?: number;
  /** Estado colapsado -- vem do uiStore para persistir entre refreshes. */
  collapsed?: boolean;
  /** Toggle do estado colapsado (vem do uiStore). */
  onToggleCollapsed?: () => void;
  className?: string;
}


/**
 * Painel lateral do chat com metricas atuais + lista de TODOs
 * (em team sessions). Padrao "informativo, somente leitura".
 *
 * Suporta dois modos:
 * - Expandido (default): 320px, conteudo completo.
 * - Colapsado: ~40px, mostra so o botao de expandir + icone do
 *   indicador "ao vivo" quando streaming.
 */
export function ChatSidePanel({
  threadId,
  isTeamSession = false,
  streaming = false,
  liveTodos,
  refreshKey,
  collapsed = false,
  onToggleCollapsed,
  className,
}: ChatSidePanelProps) {
  const { t } = useTranslation("threads");
  const metrics = useThreadMetrics(threadId);
  const todos = useThreadTodos(isTeamSession ? threadId : undefined);

  // Refresh das metricas quando o turno termina (parent incrementa
  // ``refreshKey`` ao receber evento ``done``).
  useEffect(() => {
    if (refreshKey === undefined || !threadId) return;
    void metrics.refetch();
    if (isTeamSession) void todos.refetch();
    // metrics/todos sao queries estaveis do TanStack; deps so refreshKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // ``useMemo`` antes do early return para nao quebrar a regra
  // ``react-hooks/rules-of-hooks`` (hooks devem rodar em ordem estavel).
  const todoItems = useMemo<ThreadTodoItem[]>(() => {
    if (liveTodos !== undefined) return liveTodos;
    return todos.data?.todos ?? [];
  }, [liveTodos, todos.data?.todos]);

  // Versao colapsada: largura minima + so um botao redondo.
  if (collapsed) {
    return (
      <aside
        className={cn(
          "flex flex-col items-center gap-2 rounded-2xl border border-line bg-surface p-2",
          className,
        )}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapsed}
          aria-label={t("sidePanel.expand")}
          title={t("sidePanel.expand")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div
          className={cn(
            "relative flex h-9 w-9 items-center justify-center rounded-lg",
            streaming ? "text-brand-700" : "text-ink-500",
          )}
          title={t("sidePanel.title")}
        >
          <Activity className="h-4 w-4" />
          {streaming ? (
            <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 animate-pulse rounded-full bg-brand-500" />
          ) : null}
        </div>
      </aside>
    );
  }

  const data = threadId ? metrics.data : undefined;
  const modelsCount = data?.models.length ?? 0;

  return (
    <aside
      className={cn(
        "flex flex-col gap-4 overflow-y-auto rounded-2xl border border-line bg-surface p-4",
        className,
      )}
    >
      <header className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-950">
          <Activity className="h-4 w-4 text-brand-500" />
          {t("sidePanel.title")}
        </h3>
        <div className="flex items-center gap-2">
          {streaming ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-brand-700">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-500" />
              {t("sidePanel.live")}
            </span>
          ) : null}
          {onToggleCollapsed ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapsed}
              aria-label={t("sidePanel.collapse")}
              title={t("sidePanel.collapse")}
              className="h-7 w-7"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </header>

      {!threadId ? (
        <p className="rounded-xl border border-dashed border-line bg-surface-muted px-3 py-4 text-center text-xs text-ink-500">
          {t("sidePanel.noThread")}
        </p>
      ) : (
        <>
          {isTeamSession ? (
            <p className="-mt-2 text-[11px] leading-snug text-ink-500">
              {t("sidePanel.teamModeHint")}
            </p>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <StatCard
              variant="compact"
              label={t("sidePanel.cost")}
              value={data ? formatCostUsd(data.total_cost) : "—"}
              loading={metrics.isLoading}
            />
            <StatCard
              variant="compact"
              label={t("sidePanel.tokens")}
              value={data?.total_tokens.toLocaleString("pt-BR") ?? "—"}
              loading={metrics.isLoading}
            />
            <StatCard
              variant="compact"
              label={t("sidePanel.requests")}
              value={data?.total_requests.toLocaleString("pt-BR") ?? "—"}
              loading={metrics.isLoading}
            />
            <StatCard
              variant="compact"
              label={t("sidePanel.modelsCount")}
              value={String(modelsCount)}
              loading={metrics.isLoading}
            />
          </div>

          <section className="flex flex-col gap-1.5">
            <h4 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
              {t("sidePanel.modelsTitle")}
            </h4>
            {metrics.isLoading ? (
              <p className="text-xs text-ink-500">—</p>
            ) : modelsCount === 0 ? (
              <p className="text-xs text-ink-500">{t("sidePanel.empty")}</p>
            ) : (
              <ul className="flex flex-col gap-1 text-xs">
                {data!.models.map((m) => (
                  <li
                    key={m.model}
                    className="flex items-center justify-between gap-2 rounded-lg border border-line bg-canvas px-2 py-1"
                  >
                    <span
                      className="flex min-w-0 items-center gap-2"
                      title={m.model}
                    >
                      <Cpu className="h-3 w-3 shrink-0 text-ink-500" />
                      <span className="truncate text-ink-900">{m.model}</span>
                    </span>
                    <span className="tabular shrink-0 text-ink-500">
                      {m.tokens.toLocaleString("pt-BR")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      {threadId ? (
        <AttachmentsSection threadId={threadId} refreshKey={refreshKey} />
      ) : null}

      {isTeamSession ? (
        <section className="flex flex-col gap-2 border-t border-line pt-3">
          {todos.isLoading && liveTodos === undefined && threadId ? (
            <div className="h-16 animate-pulse rounded-xl bg-ink-100/40" />
          ) : (
            <TodoList items={todoItems} />
          )}
        </section>
      ) : null}
    </aside>
  );
}

// --- Anexos section ---------------------------------------------------------

interface AttachmentsSectionProps {
  threadId: string;
  refreshKey?: number;
}

/**
 * Lista vertical de anexos da thread (input + output). Filtros via
 * pills "Todos / Enviados / Gerados" -- estado local. Cada item usa
 * ``<AttachmentBubble>``. Refetch automatico quando o parent
 * incrementa ``refreshKey`` (turno do agente terminou).
 */
function AttachmentsSection({
  threadId,
  refreshKey,
}: AttachmentsSectionProps) {
  const { t } = useTranslation("threads");
  const [kindFilter, setKindFilter] = useState<AttachmentKind | "all">("all");
  const list = useThreadAttachments(threadId, kindFilter);

  useEffect(() => {
    if (refreshKey === undefined) return;
    void list.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const items = list.data?.items ?? [];
  const count = list.data?.count ?? 0;

  return (
    <section className="flex flex-col gap-2 border-t border-line pt-3">
      <header className="flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
          <Paperclip className="h-3 w-3" />
          {t("sidePanel.attachments.title")}
          <span className="text-ink-400">({count})</span>
        </h4>
      </header>
      <div className="flex gap-1">
        {(["all", "input", "output"] as const).map((k) => (
          <Button
            key={k}
            variant={kindFilter === k ? "default" : "outline"}
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={() => setKindFilter(k)}
          >
            {t(`sidePanel.attachments.filter.${k}`)}
          </Button>
        ))}
      </div>
      {list.isLoading ? (
        <div className="h-10 animate-pulse rounded-md bg-ink-100/40" />
      ) : items.length === 0 ? (
        <p className="text-[11px] text-ink-500">
          {t("sidePanel.attachments.empty")}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((a) => (
            <li key={a.id}>
              <AttachmentBubble
                attachmentId={a.id}
                filename={a.filename}
                mimeType={a.mime_type}
                sizeBytes={a.size_bytes}
                kind={a.kind}
                className="max-w-none"
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Re-export icones usados externamente (botao de abrir Sheet em mobile).
export { Activity, BarChart3, Coins, Send, Sparkles };
